import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';
import { Platform } from '@ionic/angular/common';
import { environment } from 'src/environments/environment';
import { Authority } from './constants/authority';
import { AppAnalyticsService } from './core/services/analytics/app-analytics.service';
import { PlatformFactoryService } from './core/services/platform/platform-factory.service';
import { TranslationService } from './core/services/translate/translation.service';
import { PushSubscriberService } from './core/services/notification/push/push-subscriber.service';
import { LiveNotificationService } from './core/services/notification/live-notification.service';
import { AuthenticatedUserService } from './core/services/http/authenticated-user/authenticated-user.service';
import { User } from './core/services/http/user/interfaces/user';
import { isFeatureEnabled } from './core/utils/utils';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit {
  authority = Authority;
  simulateClass: string | undefined;
  platformClass: string | undefined;

  constructor(
    public platformFactoryService: PlatformFactoryService,
    private translationService: TranslationService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private platform: Platform,
    private pushSubscriberService: PushSubscriberService,
    private liveNotificationService: LiveNotificationService,
    private authenticatedUserService: AuthenticatedUserService,
    private appAnalyticsService: AppAnalyticsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.setNativeShellClasses();
    await this.translationService.initializeLanguage();
    this.initTPartyProviders();
    (await this.platformFactoryService.initializePlatform()).platformClassChange?.subscribe(platformClass => {
      this.platformClass = platformClass;
      this.setNativeShellClasses(platformClass);
      this.detectChanges();
    });

    if (isFeatureEnabled('notifications')) {
      this.authenticatedUserService.authenticatedUserChanged.subscribe((currentUser: User | undefined) => {
        if (currentUser !== undefined) {
          this.liveNotificationService.subscribe();
          this.pushSubscriberService.subscribe();
        }
      });
    }
  }

  onSimulateClassChange(simulateClass: string): void {
    this.simulateClass = simulateClass;
    this.detectChanges();
  }

  // @todo check for alternative way to fix ExpressionChangedAfterItHasBeenCheckedError
  private detectChanges(): void {
    this.cdRef.detectChanges();
  }

  private initDataDog(): void {
    if (environment.production) {
      datadogRum.init(environment.DataDogConfig);
    }
  }

  private initGoogleAnalyticsPageTracking(): void {
    if (environment.production) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          const pagePath = event.urlAfterRedirects;        
          // Pass the view directly to your service to let it route by platform
          void this.appAnalyticsService.trackView(pagePath);
        }
      });
    }
  }

  private initTPartyProviders(): void {
    this.platform.ready().then(() => {
      if (this.platform.is('mobileweb') || this.platform.is('desktop')) {
        this.initDataDog();
      }
      this.initGoogleAnalyticsPageTracking();
    });
  }

  /** Native shell classes on <html> so fixed chrome can use Capacitor --safe-area-inset-* (see theme/_mobile-layout.scss). */
  private setNativeShellClasses(platformClass?: string): void {
    if (!environment.mobile || !Capacitor.isNativePlatform()) {
      return;
    }
    const html = document.documentElement;
    html.classList.add('app-native');
    if (Capacitor.getPlatform() === 'android') {
      html.classList.add('app-android');
      if (!platformClass) {
        this.platformClass = 'app-android app-native';
      }
    } else if (Capacitor.getPlatform() === 'ios') {
      html.classList.add('app-ios');
    }
    (platformClass || this.platformClass || '')
      .split(/\s+/)
      .filter(Boolean)
      .forEach(className => html.classList.add(className));
  }
}
